import { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';

/**
 * Hook para buscar e gerenciar municípios do Supabase
 * @param {string} initialMunicipio - Município inicial selecionado
 * @returns {object} - { municipios, loading, error, selectedMunicipio, setSelectedMunicipio }
 */
export function useMunicipios(initialMunicipio = null) {
  const [municipios, setMunicipios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMunicipio, setSelectedMunicipio] = useState(initialMunicipio);

  useEffect(() => {
    async function fetchMunicipios() {
      try {
        setLoading(true);
        setError(null);

        // Busca municípios distintos da tabela climate_forecast_data
        const { data, error: fetchError } = await supabase
          .from('climate_forecast_data')
          .select('name_muni')
          .order('name_muni');

        if (fetchError) throw fetchError;

        // Remove duplicatas e ordena
        const municipiosUnicos = [...new Set(data.map(item => item.name_muni))].sort();
        
        setMunicipios(municipiosUnicos);
        
        // Se não tem município selecionado, seleciona o primeiro
        if (!selectedMunicipio && municipiosUnicos.length > 0) {
          setSelectedMunicipio(municipiosUnicos[0]);
        }

      } catch (err) {
        console.error('Erro ao buscar municípios:', err);
        setError(err.message || 'Erro ao carregar municípios');
      } finally {
        setLoading(false);
      }
    }

    fetchMunicipios();
  }, []);

  return {
    municipios,
    loading,
    error,
    selectedMunicipio,
    setSelectedMunicipio
  };
}

/**
 * Hook para buscar municípios com autocomplete
 * @param {string} searchTerm - Termo de busca
 * @param {string} estado - Código do estado (opcional)
 * @returns {object} - { municipios, loading, error }
 */
export function useMunicipiosAutocomplete(searchTerm, estado = null) {
  const [municipios, setMunicipios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!searchTerm || searchTerm.length < 2) {
      setMunicipios([]);
      return;
    }

    const fetchMunicipios = async () => {
      try {
        setLoading(true);
        setError(null);

        let query = supabase
          .from('municipios_brazil')
          .select('name_muni, code_state')
          .ilike('name_muni', `${searchTerm}%`)
          .order('name_muni')
          .limit(15);

        if (estado) {
          query = query.eq('code_state', estado);
        }

        const { data, error: fetchError } = await query;

        if (fetchError) throw fetchError;

        setMunicipios(data || []);
      } catch (err) {
        console.error('Erro ao buscar municípios:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    const timer = setTimeout(fetchMunicipios, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, estado]);

  return { municipios, loading, error };
}
